/**
 * FindCustomerModal — the real global customer lookup (Bill 2026-07-13 Item 6,
 * rebuilt round 2 for BACKLOG-0866/0867).
 *
 *   1. SEARCH — reuses GlobalCustomerSearch (CustSrch fan-out, kill-list
 *      filtered): name / SSN / CIF, same result cards used across RCM/RAM.
 *   2. SNAPSHOT — on select, a holistic view assembled from Core Director:
 *      contact (CustInq) + an account roster (CustAcctRelInq → per-account
 *      AcctInq) rendered in the app's DataGrid with product name, status
 *      (open/closed/dormant), current + available balance, and open date.
 *      One click to the full RCM record, RAM account maintenance, or a new cart.
 *
 * Design-system conformant (round 2): DataGrid + Pill + CSS tokens, no ad-hoc
 * inline hex. Deep-links into the existing rich surfaces (RCM CifDetail has the
 * txn/file-maint tabs) rather than rebuilding them.
 *
 * FULL SSN is shown (internal banker tool; masking is RCP/online only).
 *
 * BALANCES (BACKLOG-1355, deposit ops: "not pulling over the balances").
 * Rounds 1-2 parsed AcctInq in the browser for leaf names Core Director never
 * returns (BalAmt / LedgerBalAmt / CurBalAmt / CollectBal / AvailBalAmt), so
 * every balance rendered blank. The roster now comes from the ENRICHED
 * relationship route — GET /accounts/cust-acct-rel-inq/{cif}?enrich=1 — where
 * the Function fans out the per-account inquiries and parses them itself
 * (AccountOpeningService.CustAcctRelInqEnrichedAsync:3893; balance = AvailBal
 * else CurBal :3972, F17 OpenDt :3973, F20 AcctStat :3974, F103 card status via
 * EFTCardInq). Same production-proven source the RCM Products tab uses.
 *
 * The enriched row carries ONE balance figure and no product code, so deposit
 * rows are then topped up with a per-account AcctInq that reads only the leaf
 * names the Function itself reads off that same response: CurBal / AvailBal
 * (AccountOpeningService.cs:3972) and ProdCode (a DepInfoRec sibling of AcctStat
 * and OpenDt in real AcctInq responses — LegacyBodyBuilder.cs:267-271).
 */
import { isModuleHidden } from '@/constants/modules';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Building2, User, ChevronLeft, ExternalLink, Wrench, Plus, Ban } from '@retail-ops/panel-shell/fi';
import {
  ModalShell,
  GlobalCustomerSearch,
  Pill,
  Button,
  DataGrid,
  type DataGridColumn,
  type CustomerSearchRow,
} from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { parseCustInq } from '@/utils/custInqParser';
import { unwrapJxrlArray, txt } from '@/utils/jxrl';
import { formatSsn } from '@/utils/ssn';
import { RequestKillModal, type KillTarget } from './RequestKillModal';

export interface FindCustomerModalProps {
  open: boolean;
  onClose: () => void;
}

interface AccountLine {
  acctId: string;
  acctType: string;
  relCode: string;
  productName?: string;
  /** Raw code behind `status`: F20 account status ('' = Active), or F103 card status on a type-91 row. */
  statusCode?: string;
  status?: string; // Active | Closed | Dormant | Inactive | Mail return | Close pending
  balance?: string;
  availBalance?: string;
  openDate?: string;
  /** The server's reason a cell is blank (enriched roster only) — shown on hover. */
  note?: string;
  detailLoading: boolean;
}

interface Snapshot {
  cif: string;
  name: string;
  ssn?: string;
  phone?: string;
  email?: string;
  address?: string;
  custType?: string;
  accounts: AccountLine[];
  contactLoading: boolean;
  accountsLoading: boolean;
  error?: string;
}

// Core Director account types. 92 is the NetTeller (online banking) enrollment,
// NOT a card — the card is 91 (BACKLOG-1356). Same split the kill paths use:
// RequestKillModal maps 91 -> CardRevoke and 92 -> NetTellerRevoke, and the
// Function normalizes 91 -> "ATM" / 92 -> "NT" (KillExecutorService.cs:111-112).
const TYPE_LABEL: Record<string, string> = {
  '10': 'Checking', '20': 'Savings', '30': 'CD', '40': 'Loan',
  '50': 'Safe Deposit', '60': 'IRA', '91': 'ATM/Card', '92': 'NetTeller',
};
function typeLabel(code: string): string { return TYPE_LABEL[code] ?? (code || '—'); }

// Deposit types whose AcctInq carries a product code and a current/available
// balance pair — the only rows worth a client-side top-up inquiry.
const DEPOSIT_TYPES = new Set(['10', '20', '30', '60']);

// Core Director AcctStat codes (Common F20; blank = Active). Fallback only —
// the enriched roster ships the server's own description (DescribeAcctStat).
const STATUS_LABEL: Record<string, string> = {
  '': 'Active', M: 'Mail return', P: 'Close pending', C: 'Closed', D: 'Dormant', I: 'Inactive',
};
/** Tone for an F20 account-status code. */
function statusVariant(code?: string): 'ok' | 'warn' | 'err' | 'muted' {
  const c = (code ?? '').trim().toUpperCase();
  if (c === '') return 'ok';
  if (c === 'C') return 'err';
  if (c === 'M' || c === 'P' || c === 'D' || c === 'I') return 'warn';
  return 'muted';
}
/** Tone for an F103 card-status code (ATM rows) — A is the only healthy one. */
function cardStatusVariant(code?: string): 'ok' | 'warn' | 'err' | 'muted' {
  const c = (code ?? '').trim().toUpperCase();
  if (c === '') return 'muted';
  if (c === 'A') return 'ok';
  if (c === 'C' || c === 'D' || c === 'E' || c === 'H') return 'err';
  return 'warn';
}

function fmtMoney(raw?: string): string {
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
function fmtDate(raw?: string): string {
  if (!raw) return '—';
  // jXRL dates arrive as YYYY-MM-DD or YYYYMMDD.
  const m = /^(\d{4})-?(\d{2})-?(\d{2})/.exec(raw);
  if (!m) return raw;
  return `${m[2]}/${m[3]}/${m[1]}`;
}

/**
 * Depth-first scalar lookup, a port of the Function's own DeepFindScalar
 * (apps/PCBJxChangeApi/Services/AccountOpeningService.cs:4045-4071): same
 * pre-order traversal, same tolerance for numbers and for jXRL's
 * `{ "#text": "…" }` wrappers. Reading a Core response the way the server reads
 * it is the point — the hand-rolled extractor this replaces matched invented
 * key names and returned nothing (BACKLOG-1355).
 */
function deepFindScalar(node: unknown, name: string, depth = 0): string | undefined {
  if (node == null || depth > 12) return undefined;
  if (Array.isArray(node)) {
    for (const item of node) {
      const hit = deepFindScalar(item, name, depth + 1);
      if (hit !== undefined) return hit;
    }
    return undefined;
  }
  if (typeof node !== 'object') return undefined;
  for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
    if (k === name) {
      if (typeof v === 'string') return v; // '' is meaningful: blank F20 = Active
      if (typeof v === 'number') return String(v);
      if (v && typeof v === 'object') {
        const text = (v as Record<string, unknown>)['#text'];
        if (typeof text === 'string') return text;
      }
    }
    const hit = deepFindScalar(v, name, depth + 1);
    if (hit !== undefined) return hit;
  }
  return undefined;
}

export function FindCustomerModal({ open, onClose }: FindCustomerModalProps): JSX.Element | null {
  const client = useApiClient();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  // Officer kill requests (Bill 2026-09-15): any record on the customer can be
  // sent to SysOps. Open requests show instead of the button.
  const [killTarget, setKillTarget] = useState<KillTarget | null>(null);
  const [openKills, setOpenKills] = useState<ReadonlySet<string>>(new Set());

  const prodNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of config.products ?? []) {
      const code = (p as { code?: string }).code;
      const name = (p as { name?: string }).name;
      if (code && name) m.set(code.toUpperCase(), name);
    }
    return m;
  }, [config.products]);

  useEffect(() => { if (!open) setSnap(null); }, [open]);

  const snapCif = snap?.cif;
  useEffect(() => {
    if (!snapCif) return;
    let cancelled = false;
    void (async () => {
      try {
        const [pending, verified] = await Promise.all([
          client.retail.killList.list({ status: 'pending-verification', top: 500 }),
          client.retail.killList.list({ status: 'verified', top: 500 }),
        ]);
        if (cancelled) return;
        setOpenKills(new Set([...(pending.entries ?? []), ...(verified.entries ?? [])].map((e) => e.targetId)));
      } catch {
        // Fail-soft: without the list every record simply offers the request.
      }
    })();
    return () => { cancelled = true; };
  }, [client, snapCif]);

  const loadSnapshot = useCallback(async (row: CustomerSearchRow) => {
    setSnap({
      cif: row.cifId, name: row.name, ssn: row.taxId, phone: row.phone,
      address: row.address, custType: row.custType, accounts: [],
      contactLoading: true, accountsLoading: true,
    });

    // Contact (CustInq).
    void (async () => {
      try {
        const env = await client.accounts.custInq(row.cifId);
        const inq = parseCustInq((env as { data?: unknown })?.data ?? env, row.cifId);
        setSnap((p) => p && p.cif === row.cifId ? {
          ...p,
          phone: inq.success ? (inq.phoneCell || inq.phoneHome || p.phone) : p.phone,
          email: inq.success ? (inq.email || p.email) : p.email,
          address: inq.success
            ? ([inq.addr, inq.city && inq.state ? `${inq.city}, ${inq.state}` : inq.city, inq.zip].filter(Boolean).join(' ') || p.address)
            : p.address,
          contactLoading: false,
        } : p);
      } catch {
        setSnap((p) => p && p.cif === row.cifId ? { ...p, contactLoading: false } : p);
      }
    })();

    // Per-account top-up: product name + the current/available balance pair the
    // enriched row cannot carry (it ships a single figure — AvailBal else
    // CurBal). Fail-soft: on any error the row keeps whatever the server gave
    // us and simply stops showing the loading ellipsis.
    const hydrate = (line: AccountLine): void => {
      void (async () => {
        try {
          const inqRaw = await client.accounts.acctInq(line.acctId, line.acctType);
          const env = inqRaw as unknown as Record<string, unknown>;
          const data = (env['data'] ?? env['Data']) as unknown;
          const prodCode = deepFindScalar(data, 'ProdCode');
          const cur = deepFindScalar(data, 'CurBal');
          const avail = deepFindScalar(data, 'AvailBal');
          // Only used by the raw fallback below; the enriched roster already
          // carries F20/F17 and must not be overwritten by a second read.
          const statCode = deepFindScalar(data, 'AcctStat');
          const openDt = deepFindScalar(data, 'OpenDt');
          setSnap((p) => p && p.cif === row.cifId ? {
            ...p,
            accounts: p.accounts.map((a) => a.acctId === line.acctId && a.acctType === line.acctType ? {
              ...a,
              productName: prodCode ? (prodNameByCode.get(prodCode.toUpperCase()) ?? prodCode) : a.productName,
              statusCode: a.status !== undefined ? a.statusCode : statCode,
              status: a.status ?? (statCode !== undefined ? (STATUS_LABEL[statCode.trim()] ?? statCode) : undefined),
              balance: cur ?? a.balance,
              availBalance: avail ?? a.availBalance,
              openDate: a.openDate ?? openDt,
              detailLoading: false,
            } : a),
          } : p);
        } catch {
          setSnap((p) => p && p.cif === row.cifId ? {
            ...p,
            accounts: p.accounts.map((a) => a.acctId === line.acctId && a.acctType === line.acctType
              ? { ...a, detailLoading: false } : a),
          } : p);
        }
      })();
    };

    // Accounts — enriched roster first, raw relationship list as the safety net.
    void (async () => {
      try {
        const enriched = await client.accounts.custAcctRelInqEnriched(row.cifId);
        const lines: AccountLine[] = (enriched?.accounts ?? []).map((r) => {
          const isCard = r.acctType === '91';
          const note = r.note ?? undefined;
          // A card row reports F103; everything else reports F20. The server
          // describes a NULL F20 as "Active" (DescribeAcctStat), and for a
          // non-card row a note means its AcctInq failed — so in that case show
          // the dash plus the reason rather than asserting a status Core never
          // sent.
          const statusCode = (isCard ? r.cardStat : r.acctStat) ?? undefined;
          const status = isCard
            ? (r.cardStatDesc ?? r.cardStat ?? undefined)
            : (note ? undefined : (r.acctStatDesc ?? STATUS_LABEL[(r.acctStat ?? '').trim()]));
          return {
            acctId: r.acctId,
            acctType: r.acctType,
            relCode: r.acctRelCode ?? r.relCode ?? '',
            statusCode,
            status,
            // A card row has no balance of its own (the RCM Products tab blanks it too).
            balance: !isCard && r.balance != null ? String(r.balance) : undefined,
            openDate: r.openedOn ?? undefined,
            note,
            detailLoading: DEPOSIT_TYPES.has(r.acctType),
          };
        }).filter((l) => l.acctId && l.acctType);

        if (lines.length > 0) {
          setSnap((p) => p && p.cif === row.cifId ? { ...p, accounts: lines, accountsLoading: false } : p);
          for (const line of lines) if (line.detailLoading) hydrate(line);
          return;
        }
        // Zero rows is ambiguous — the route answers with an empty list both for
        // a customer with no accounts AND for a failed CustAcctRelInq — so let
        // the raw call below say which it is.
      } catch {
        // Enriched route unavailable: fall through so the roster never goes dark.
      }

      try {
        const res = await client.accounts.custAcctRelInq(row.cifId);
        const raw = res as unknown as Record<string, unknown>;
        const dataNode = (raw['data'] as Record<string, unknown> | undefined)
          ?? (raw['Data'] as Record<string, unknown> | undefined) ?? raw;
        const recs = unwrapJxrlArray<Record<string, unknown>>(dataNode, 'CustAcctRelInqRecArray', 'CustAcctRelInqRec');
        const lines: AccountLine[] = recs.map((r) => {
          const acctObj = r['AcctId'];
          let acctId = '', acctType = '';
          if (typeof acctObj === 'string') acctId = acctObj;
          else if (acctObj && typeof acctObj === 'object') {
            const inner = acctObj as Record<string, unknown>;
            acctId = txt(inner['AcctId']); acctType = txt(inner['AcctType']);
          }
          if (!acctType) acctType = txt(r['AcctType']);
          return { acctId, acctType, relCode: txt(r['AcctRelCode']) || txt(r['RelCode']), detailLoading: true };
        }).filter((l) => l.acctId);

        setSnap((p) => p && p.cif === row.cifId ? { ...p, accounts: lines, accountsLoading: false } : p);

        // No server-side enrichment on this path — one AcctInq per account fills
        // product / balances / status / open date.
        for (const line of lines) hydrate(line);
      } catch {
        setSnap((p) => p && p.cif === row.cifId ? { ...p, accountsLoading: false, error: 'Could not load accounts.' } : p);
      }
    })();
  }, [client, prodNameByCode]);

  if (!open) return null;
  const go = (path: string): void => { onClose(); navigate(path); };

  const columns: DataGridColumn<AccountLine>[] = [
    {
      key: 'acct', label: 'Account', width: '30%',
      render: (a) => (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }}>{a.acctId}</span>
          <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>
            {a.productName ?? typeLabel(a.acctType)}
            {a.relCode ? ` · ${a.relCode}` : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'type', label: 'Type',
      render: (a) => <Pill variant="info">{typeLabel(a.acctType)}</Pill>,
    },
    {
      // Account status (F20) on every row except an ATM row, which reports its
      // card status (F103) instead — a row is one or the other.
      key: 'status', label: 'Status',
      render: (a) => a.detailLoading ? <span style={{ color: 'var(--ink-400)' }}>…</span>
        : a.status === undefined
          ? <span title={a.note} style={{ color: 'var(--ink-400)' }}>—</span>
          : (
            <span title={a.note}>
              <Pill variant={a.acctType === '91' ? cardStatusVariant(a.statusCode) : statusVariant(a.statusCode)}>
                {a.status}
              </Pill>
            </span>
          ),
    },
    {
      key: 'balance', label: 'Balance', numeric: true,
      render: (a) => a.detailLoading ? <span style={{ color: 'var(--ink-400)' }}>…</span> : (
        <span title={a.note} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }}>{fmtMoney(a.balance)}</span>
          {a.availBalance && a.availBalance !== a.balance && (
            <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>avail {fmtMoney(a.availBalance)}</span>
          )}
        </span>
      ),
    },
    { key: 'opened', label: 'Opened', render: (a) => <span style={{ fontSize: 12 }} title={a.note}>{fmtDate(a.openDate)}</span> },
    {
      key: 'action', label: '', width: '18%',
      render: (a) => (
        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {!isModuleHidden('RAM') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => go(`/ram?cifId=${encodeURIComponent(snap!.cif)}&name=${encodeURIComponent(snap!.name)}&acctId=${encodeURIComponent(a.acctId)}&acctType=${encodeURIComponent(a.acctType)}`)}
            >
              <Wrench size={12} aria-hidden style={{ marginRight: 4 }} /> Maintain
            </Button>
          )}
          {a.acctType !== '40' && (openKills.has(a.acctId) ? (
            <Pill variant="warn">Kill requested</Pill>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setKillTarget({ id: a.acctId, type: a.acctType, label: `${a.productName ?? typeLabel(a.acctType)} ${a.acctId}` })}
            >
              <Ban size={12} aria-hidden style={{ marginRight: 4 }} /> Request kill
            </Button>
          ))}
        </span>
      ),
    },
  ];

  return (
    <>
    <ModalShell open={open} onClose={onClose} title={snap ? 'Customer' : 'Find customer'} size="xl" ariaLabel="Find customer">
      {!snap ? (
        <div style={{ minHeight: 360 }}>
          <GlobalCustomerSearch onSelect={(row) => void loadSnapshot(row)} placeholder="Search by name, SSN, or CIF…" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
          <button
            type="button"
            onClick={() => setSnap(null)}
            style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 0, color: 'var(--pcb-blue)', cursor: 'pointer', fontSize: 13, padding: 0 }}
          >
            <ChevronLeft size={14} aria-hidden /> Back to search
          </button>

          {/* Identity + contact */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
            <span style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--brand-fill)', color: 'var(--on-brand)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden>
              {snap.custType?.toLowerCase().includes('bus') ? <Building2 size={22} /> : <User size={22} />}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--pcb-blue-dark)' }}>{snap.name}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-600)', display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>CIF {snap.cif}</span>
                {snap.ssn && <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{formatSsn(snap.ssn)}</span>}
                {snap.custType && <Pill variant="muted">{snap.custType}</Pill>}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-7)', flexWrap: 'wrap', fontSize: 13, color: 'var(--ink-700)' }}>
            {snap.phone && <span>📞 {snap.phone}</span>}
            {snap.email && <span>✉ {snap.email}</span>}
            {snap.address && <span>📍 {snap.address}</span>}
            {snap.contactLoading && <span style={{ color: 'var(--ink-500)' }}>Loading contact…</span>}
          </div>

          {/* Account roster (DataGrid — app-canonical) */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink-600)', marginBottom: 'var(--sp-3)' }}>
              Accounts {snap.accountsLoading ? '' : `(${snap.accounts.length})`}
            </div>
            {snap.accountsLoading ? (
              <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>Loading accounts…</div>
            ) : (
              <DataGrid
                columns={columns}
                rows={snap.accounts}
                rowKey={(a) => `${a.acctId}-${a.acctType}`}
                emptyMessage={snap.error ?? 'No accounts on file.'}
              />
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap', paddingTop: 'var(--sp-4)', borderTop: '1px solid var(--surface-border)' }}>
            <Button variant="primary" onClick={() => go(`/rcm/${encodeURIComponent(snap.cif)}`)}>
              <ExternalLink size={14} aria-hidden style={{ marginRight: 6 }} /> Open full record
            </Button>
            {!isModuleHidden('RAM') && (
              <Button variant="ghost" onClick={() => go(`/ram?cifId=${encodeURIComponent(snap.cif)}&name=${encodeURIComponent(snap.name)}`)}>
                <Wrench size={14} aria-hidden style={{ marginRight: 6 }} /> Account maintenance
              </Button>
            )}
            <Button variant="ghost" onClick={() => go('/rao/new')}>
              <Plus size={14} aria-hidden style={{ marginRight: 6 }} /> New account for this customer
            </Button>
            {openKills.has(snap.cif) ? (
              <Pill variant="warn">CIF kill requested</Pill>
            ) : (
              <Button variant="ghost" onClick={() => setKillTarget({ id: snap.cif, type: 'CIF', label: `Customer record (CIF ${snap.cif})` })}>
                <Ban size={14} aria-hidden style={{ marginRight: 6 }} /> Request CIF kill
              </Button>
            )}
          </div>
        </div>
      )}
    </ModalShell>
    {snap && (
      <RequestKillModal
        target={killTarget}
        cif={snap.cif}
        customerName={snap.name}
        onClose={() => setKillTarget(null)}
        onRequested={(id) => {
          setOpenKills((prev) => new Set([...prev, id]));
          setKillTarget(null);
        }}
      />
    )}
    </>
  );
}

export default FindCustomerModal;
